import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { customFieldEndpoints } from '@/lib/api/endpoints';
import { customFieldKeys } from '@/lib/queryKeys';
import type { CustomFieldAssociation, CreateAssociationDto } from '@/types/custom-fields';

interface CreateAssociationVariables {
  definitionId: string;
  dto: CreateAssociationDto;
}

interface UseCreateAssociationOptions {
  /**
   * When true, skip the auto success toast. Callers that attach a field as one
   * step of a larger save (the detail-page "Add custom field" dialog attaches,
   * then writes the value) emit a single toast for the whole action instead.
   */
  silent?: boolean;
}

export function useCreateAssociation(options: UseCreateAssociationOptions = {}) {
  const queryClient = useQueryClient();
  const silent = options.silent ?? false;
  return useApiMutation<CustomFieldAssociation, CreateAssociationVariables>({
    mutationFn: ({ definitionId, dto }) =>
      apiPost<CustomFieldAssociation>(
        operationsClient,
        customFieldEndpoints.definitionAssociations(definitionId),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: customFieldKeys.all() });
      if (!silent) toast.success('Association created');
    },
  });
}
